import React from 'react';
import { Button, Select } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import AccordionPanel from './AccordionPanel';
import {
  themedBlockProps, themedBlockResolveData, themedBlockResolveFields,
} from '../../../puck/themedBlocks';
import InspectorRow from '../../../puck/fields/InspectorRow';
import { notifyPanelPreviewToggle } from '../../../utils/accordionPanelsRegistry';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { puckBorderField } from '../../../puck/fields/puckBorderField';
import { puckHorizontalAlignField } from '../../../puck/fields/puckHorizontalAlignField';
import { puckVerticalAlignField } from '../../../puck/fields/puckVerticalAlignField';
import { puckColorField } from '../../../puck/fields/createPuckColorField';
import { puckTextBackgroundField } from '../../../puck/fields/puckTextBackgroundField';
import { accordionPanelDefaultProps } from '../../../../shared/boxes/accordionPanel/accordionPanelProps';
import { puckSectionField, puckStyleGallerySectionField } from '../../../puck/fields/puckSectionField';
import { withThemedFieldValues } from '../../../puck/fields/themedFieldValues';
import { puckTextRolesField } from '../../../puck/fields/TextRolesField';
import { puckThemeStyleField } from '../../../puck/fields/ThemeStyleSelectField';
import { puckShadowField } from '../../../puck/fields/puckShadowField';
import { ACCORDION_PANEL_GROUP, ACCORDION_PANEL_LABELS } from '../../../../shared/boxes/accordionPanel/accordionPanelLabels';
import { SHADOW_LABEL } from '../../../../shared/boxes/shadowStyles';

/**
 * Réglage propre au SITE : dans un document, un volet est toujours déplié.
 * Il ne descend donc pas dans les props communes.
 */
const ACCORDION_OPEN_BEHAVIOR_OPTIONS = [
  { value: 'exclusive', label: 'Une seule ouverte à la fois' },
  { value: 'independent', label: 'Indépendante' },
];

const ACCORDION_OPEN_BEHAVIOR_HELP = '« Une seule ouverte à la fois » : ouvrir ce volet referme les autres volets de la page réglés pareil. « Indépendante » : ce volet s\'ouvre et se ferme sans influencer les autres.';

/** Select « Ouverture » + ⓘ : le comportement lie plusieurs volets entre eux. */
function OpenBehaviorField({ value, onChange }) {
  const { itemProps } = useSelectedItemProps();
  return (
    /* Groupe et non <label> : un <label> propagerait hover/clic à son
       premier contrôle (cf. puckBorderField). */
    <InspectorRow label="Ouverture" hint={ACCORDION_OPEN_BEHAVIOR_HELP} layout="stacked">
      <Select
        size="xs"
        data={ACCORDION_OPEN_BEHAVIOR_OPTIONS}
        value={value || 'exclusive'}
        onChange={(next) => {
          if (next) onChange(next);
        }}
        allowDeselect={false}
        comboboxProps={{ withinPortal: true }}
      />
      {/* Aperçu : bascule l'état ouvert/fermé du volet sur le canvas
          (état local, non persisté — registre accordionPanelsRegistry). */}
      <Button
        mt="xs"
        size="compact-xs"
        variant="light"
        fullWidth
        leftSection={<FontAwesomeIcon icon={icon({ name: 'arrows-left-right', style: 'light' })} style={{ fontSize: 14 }} />}
        onClick={() => {
          if (itemProps.id) notifyPanelPreviewToggle(itemProps.id);
        }}
      >
        Ouvrir / fermer le volet (aperçu)
      </Button>
    </InspectorRow>
  );
}

const accordionPanelConfig = {
  label: 'Volet',
  // Démo au drop : l'utilisateur voit le rendu et remplace par ses données.
  defaultProps: {
    ...accordionPanelDefaultProps(),
    question: 'Quels sont vos délais ?',
    answer: '<p>Nous livrons généralement sous 2 à 4 semaines selon le périmètre du projet.</p>',
  },
  // Question et réponse s'éditent UNIQUEMENT sur le canvas (rich text inline
  // + toolbar) : un éditeur sidebar en double se désynchronisait.
  fields: withThemedFieldValues('accordion', {
    _section1: puckStyleGallerySectionField(ACCORDION_PANEL_GROUP),
    themeStyleId: puckThemeStyleField('accordion'),

    _sectionStyle: puckSectionField('Style'),
    panelColor: puckColorField(ACCORDION_PANEL_LABELS.panelColor, { allowTransparent: true }),
    answerBackground: puckTextBackgroundField(ACCORDION_PANEL_LABELS.answerBackground),
    shadow: puckShadowField(SHADOW_LABEL),
    border: puckBorderField('Bordures'),

    _sectionText: puckSectionField('Typographie'),
    textStyles: puckTextRolesField([{ key: 'question', label: 'Question' }, { key: 'answer', label: 'Réponse' }]),

    _section2: puckSectionField('Affichage'),
    openBehavior: {
      type: 'custom',
      label: 'Ouverture',
      render: (props) => <OpenBehaviorField {...props} />,
    },
    alignHorizontal: puckHorizontalAlignField(),
    alignVertical: puckVerticalAlignField(),

  }),
  // Déposé : style par défaut du thème. Réglé à la main : détaché du thème.
  resolveData: themedBlockResolveData('accordion'),
  resolveFields: themedBlockResolveFields('accordion'),
  render: (props) => <AccordionPanel {...themedBlockProps('accordion', props, props.puck?.metadata)} />,
};

export default accordionPanelConfig;
